import * as React from "react";
import moment from "moment";
import groupby from "lodash.groupby";
import { IChartDataItemV2 } from "@iliotech/types";
import { ClickableLegendItem } from "@iliotech/component-library";
import {
  addAlpha,
  convertDataToQuarter,
  convertDataToYears,
  getBackgroundColor,
  reduceArrays,
} from "@iliotech/data-wire/src/utils";
import {
  IStructureGrouping,
  useGlobalCurrency,
  useStructureGrouping,
} from "@iliotech/data-wire";
import { useSmallScreen } from "@iliotech/data-wire/src/reactHooks/hooks/useSmallScreen";
import { DRILL_DOWNS } from "@iliotech/data-wire/src/utils";
import { CHART_DATE_FORMAT } from "@iliotech/data-wire/src/constants/constants";

interface IIncomeItem {
  date: moment.Moment;
  instrumentName: string;
  amountBase: number;
  amountLocal: number;
  currencyLocalSymbol: string;
}

interface UseIncomeFutureWidgetProps {
  futureIncome: IIncomeItem[];
  structureContext?: IStructureGrouping;
}

export const useProjectedFutureIncomeWidget = ({
  futureIncome,
  structureContext,
}: UseIncomeFutureWidgetProps) => {
  const { currencyFormatter } = useGlobalCurrency();
  const { selectedGroup, setSelectedGroup, grouping } =
    structureContext || useStructureGrouping();
  const isMobile = useSmallScreen();

  const futureDataTotalAbsolute = React.useMemo(() => {
    return (futureIncome || [])
      .map((item) => item.amountBase)
      .reduce((a, b) => a + b, 0);
  }, [futureIncome]);

  const { futureIncomeTotals, futureIncomeData } = React.useMemo(() => {
    const filterField = grouping.field;
    const filterLabel = grouping.labelField;
    const filterId = selectedGroup?.code;
    const aggregateField = (
      !filterId
        ? filterField
        : DRILL_DOWNS[grouping.field]?.field || filterField
    ) as keyof IIncomeItem;

    const aggregateLabel = (
      !filterId
        ? filterLabel
        : DRILL_DOWNS[grouping.field]?.labelField || filterLabel
    ) as keyof IIncomeItem;

    const getChartItemFromIncomePoint = (item: any): IChartDataItemV2 => {
      const getColor = () => {
        const color =
          grouping?.getColor?.(item[aggregateField] as string) ||
          getBackgroundColor((item[aggregateField] as any) || "null");
        if (filterId) {
          return item.isLong ? color : addAlpha(color, 0.6);
        }
        return color;
      };

      return {
        category: item[aggregateLabel as keyof IIncomeItem] as any,
        name: moment(item.date).format("MMM 'YY"),
        date: moment(item.date).toDate() as any,
        id: item.instrumentName,
        color: getColor(),
        value: item.amountBase,
        tooltipExtra: item.amountBase
          ? [
              {
                label: item.instrumentName,
                value:
                  item.amountLocal === item.amountBase
                    ? currencyFormatter!(item.amountBase)
                    : `${item.currencyLocalSymbol} ${
                        item.amountLocal
                      } / ${currencyFormatter!(item.amountBase)}`,
              },
            ]
          : [],
      } as any;
    };

    const processIncomeData = (data: any[]) => {
      const groupedData: { [grouping: string]: IChartDataItemV2[] } = {};
      const filteredData = filterId
        ? (data || []).filter((s: any) => s[filterField] === filterId)
        : data;

      filteredData?.forEach((item) => {
        groupedData[item[aggregateField] as string] =
          groupedData[item[aggregateField] as string] || [];

        groupedData[item[aggregateField] as string].push(
          getChartItemFromIncomePoint(item as any)
        );
      });

      let incomeData = reduceArrays(Object.values(groupedData));

      const itemsInCategoryAxis = Object.values(
        groupby(incomeData, (item: IChartDataItemV2) => item?.name)
      ).length;

      if (itemsInCategoryAxis > 20) {
        incomeData = isMobile
          ? convertDataToYears(incomeData)
          : convertDataToQuarter(incomeData);
      } else if (itemsInCategoryAxis > 15 && isMobile) {
        incomeData = convertDataToQuarter(incomeData);
      }

      let totals: ClickableLegendItem[] = [];
      const rawValue = [...incomeData]
        .map((item) => item.value)
        .reduce((a, b) => a + b, 0);

      totals.push({
        label: "Total",
        onClick: () => onReset(),
        rawValue,
        value: currencyFormatter!(rawValue),
      });

      Object.keys(groupedData).forEach((key) => {
        const dataForLegend = groupedData[key];
        const totalValue = dataForLegend
          .map((item) => item.value)
          .reduce((a, b) => a + b, 0);

        totals.push({
          label: dataForLegend[0]?.category,
          onClick: () => onItemClick(key, dataForLegend[0]?.category),
          value: currencyFormatter!(totalValue),
          rawValue: totalValue,
          color: groupedData[key][0].color,
        });
      });

      return {
        data: incomeData,
        totals: totals.filter(
          (item) => item.rawValue! >= 1 || item.rawValue! <= -1
        ),
      };
    };

    const onItemClick = (code: string, label: string) => {
      if (!selectedGroup?.code) {
        setSelectedGroup?.({ code, label });
      } else {
        setSelectedGroup?.(undefined);
      }
    };

    const onReset = () => {
      setSelectedGroup?.(undefined);
    };

    const processedData = processIncomeData(futureIncome);

    return {
      futureIncomeTotals: processedData.totals,
      futureIncomeData: processedData.data,
    };
  }, [
    futureIncome,
    selectedGroup,
    grouping,
    currencyFormatter,
    setSelectedGroup,
    isMobile,
  ]);

  const futureDate = React.useMemo(
    () =>
      `${moment().add(1, "days").format(CHART_DATE_FORMAT)} - ${moment()
        .add(365, "days")
        .format(CHART_DATE_FORMAT)}`,
    []
  );

  const futureIncomeTitle = React.useMemo(
    () =>
      !futureIncomeData.length
        ? `No projected income from ${
            !selectedGroup?.code ? grouping.label : selectedGroup.label
          }`
        : `Projected future income from ${
            !selectedGroup?.code ? grouping.label : selectedGroup.label
          }`,
    [selectedGroup, futureIncomeData, grouping.label]
  );

  return {
    futureDataTotalAbsolute,
    futureIncomeTotals,
    futureIncomeData,
    futureIncomeTitle,
    futureDate,
    selectedGroup,
  };
};
